import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ReactNode } from "react";
import {
    IDraggingType,
    IDragState,
    IMarquee,
    IPointerPosition,
    IPointerRatio,
    ISize,
    IUniversalItem,
} from "userful-chronos-app-common-js/dist/itemsControl/itemsControl";

const getInitialDragState = (): IDragState => ({
    items: [],
    primaryId: null,
    fly: null,
    pointer: null,
    pointerOffset: null,
    pointerRatio: null,
    initialSize: null,
    useInitialSize: true,
});

export interface IItemsControlStore {
    draggingType: IDraggingType;
    marquee: IMarquee | null;
    selectedItems: IUniversalItem[];
    preselectedItems: IUniversalItem[];
    draggedItems: IUniversalItem[];
    overDropzone: IUniversalItem | null;
    drag: IDragState;
}

const initialState: IItemsControlStore = {
    draggingType: null,
    marquee: null,
    selectedItems: [],
    preselectedItems: [],
    draggedItems: [],
    overDropzone: null,
    drag: getInitialDragState(),
};

const mainSlice = createSlice({
    name: "itemsControl",
    initialState,
    reducers: {
        updateMarquee(state: IItemsControlStore, action: PayloadAction<IMarquee | null>) {
            // Ignore if we are already in MOVE or COPY mode
            if (state.draggingType !== "MOVE" && state.draggingType !== "COPY") {
                if (!action.payload) {
                    state.draggingType = null;
                } else {
                    state.draggingType = "SELECTION";
                }

                state.marquee = action.payload;
            }
        },
        updateSelectedItems(state: IItemsControlStore, action: PayloadAction<IUniversalItem[]>) {
            state.selectedItems = action.payload;
        },
        updatePreselectedItems(state: IItemsControlStore, action: PayloadAction<IUniversalItem[]>) {
            state.preselectedItems = action.payload;
        },
        updateDraggedItems(state: IItemsControlStore, action: PayloadAction<IUniversalItem[]>) {
            state.draggedItems = action.payload;
            state.drag.items = action.payload;
        },
        updateOverDropzone(state: IItemsControlStore, action: PayloadAction<IUniversalItem | null>) {
            state.overDropzone = action.payload;
        },
        startDrag(
            state: IItemsControlStore,
            action: PayloadAction<{
                items: IUniversalItem[];
                primaryId: string;
                fly?: ReactNode | null;
                pointer?: IPointerPosition | null;
                offset?: IPointerPosition | null;
                ratio?: IPointerRatio | null;
                initialSize?: ISize | null;
                useInitialSize?: boolean;
            }>,
        ) {
            const {
                items,
                primaryId,
                fly = null,
                pointer = null,
                offset = null,
                ratio = null,
                initialSize = null,
                useInitialSize = true,
            } = action.payload;

            state.draggingType = "MOVE";
            state.draggedItems = items;
            state.overDropzone = null;
            // Keep the current selection in sync with the dragged items so rows/cards stay visibly selected while dragging.
            state.selectedItems = items;
            state.preselectedItems = [];
            state.marquee = null;
            state.drag = {
                items,
                primaryId,
                fly,
                pointer,
                pointerOffset: offset,
                pointerRatio: ratio,
                initialSize,
                useInitialSize,
            };
        },
        updatePointer(state: IItemsControlStore, action: PayloadAction<IPointerPosition | null>) {
            state.drag.pointer = action.payload;
        },
        endDrag(state: IItemsControlStore) {
            state.draggingType = null;
            state.draggedItems = [];
            state.overDropzone = null;
            state.drag = getInitialDragState();
        },
        clearSelectionState(state: IItemsControlStore) {
            state.selectedItems = [];
            state.preselectedItems = [];
            state.draggedItems = [];
            state.draggingType = null;
            state.marquee = null;
            state.overDropzone = null;
            state.drag = getInitialDragState();
        },
    },
});

export const ItemsControlActions = mainSlice.actions;

export default mainSlice.reducer;
